/**
 * Gift Cards API Client
 *
 * Handles gift card management for sharing SMART tokens with others.
 * Extends BaseApiClient for authentication, error handling, and type-safe requests.
 *
 * API Endpoints (from backend):
 * - POST /organizations/{org_id}/gift-cards - Create
 * - GET /organizations/{org_id}/gift-cards - List
 * - GET /organizations/{org_id}/gift-cards/{id} - Get
 * - POST /organizations/{org_id}/gift-cards/{id}/cancel - Cancel
 * - POST /gift-cards/redeem - Redeem (global, no orgId needed)
 */

import type {
  GiftCard,
  GiftCardWithMetadata,
  CreateGiftCardRequest,
  CreateGiftCardResponse,
  RawCreateGiftCardResponse,
  RedeemGiftCardResponse,
  ListGiftCardsParams,
  ListGiftCardsResponse,
  RawListGiftCardsResponse,
} from '@/types/giftCard.types';
// GW-6533: Validate orgId at API client boundary so frontend regressions
// throw a distinct InvalidOrgIdError before the network call leaves the
// browser, instead of producing a noisy 4xx after a round-trip.
import { assertOrgId } from '@/types/orgId';

import { BaseApiClient } from './baseApiClient';

/**
 * Gift Cards API client class
 * Manages gift card creation, redemption, and lifecycle
 */
class GiftCardsApiClient extends BaseApiClient {
  // ============================================================================
  // Gift Card Management
  // ============================================================================

  /**
   * Transform raw backend response item to flattened GiftCardWithMetadata
   */
  private transformGiftCardResponse(
    item: RawListGiftCardsResponse['giftCards'][0]
  ): GiftCardWithMetadata {
    return {
      // Spread the nested gift card data
      ...item.giftCard,
      // Add the metadata fields from the wrapper
      canRedeem: item.canRedeem,
      canCancel: item.canCancel,
      daysRemaining: item.daysRemaining,
      purchaserName: item.purchaserName,
      purchaserEmail: item.purchaserEmail,
      recipientName: item.recipientName,
    };
  }

  /**
   * List gift cards for an organization
   *
   * HTTP Method: GET /organizations/:orgId/gift-cards
   * Authorization: Required (JWT token)
   * RBAC: Any authenticated organization member
   *
   * @param orgId - Organization ID
   * @param params - Optional query parameters for filtering and pagination
   * @returns List of gift cards with pagination
   * @throws {ApiError} When API request fails
   */
  async listGiftCards(orgId: string, params?: ListGiftCardsParams): Promise<ListGiftCardsResponse> {
    assertOrgId(orgId);
    const query = new URLSearchParams();

    if (params?.limit) {
      query.set('limit', String(params.limit));
    }
    if (params?.nextToken) {
      query.set('nextToken', params.nextToken);
    }

    const queryString = query.toString();
    const path = `/organizations/${orgId}/gift-cards${queryString ? `?${queryString}` : ''}`;

    // Fetch raw response with nested structure from backend
    const rawResponse = await this.get<RawListGiftCardsResponse>(path);

    // Transform to flattened structure for frontend use
    return {
      giftCards: rawResponse.giftCards.map((item) => this.transformGiftCardResponse(item)),
      total: rawResponse.total,
      nextToken: rawResponse.nextToken,
    };
  }

  /**
   * Get a specific gift card by ID
   *
   * HTTP Method: GET /organizations/:orgId/gift-cards/:giftCardId
   * Authorization: Required (JWT token)
   * RBAC: Any authenticated organization member
   *
   * @param orgId - Organization ID
   * @param giftCardId - Gift card ID
   * @returns Gift card details
   * @throws {ApiError} When API request fails or gift card not found (404)
   */
  async getGiftCard(orgId: string, giftCardId: string): Promise<GiftCard> {
    assertOrgId(orgId);
    return this.get<GiftCard>(`/organizations/${orgId}/gift-cards/${giftCardId}`);
  }

  /**
   * Create a new gift card
   *
   * HTTP Method: POST /organizations/:orgId/gift-cards
   * Authorization: Required (JWT token)
   * RBAC: org_admin or global_admin only
   *
   * Creates a new gift card and deducts the token amount from the organization's balance.
   * The response includes the redemption URL which should be shared with the recipient.
   *
   * @param orgId - Organization ID
   * @param request - Gift card creation parameters
   * @returns Created gift card with redemption URL
   * @throws {ApiError} When API request fails:
   *   - 400: Invalid request (negative amount, etc.)
   *   - 402: Insufficient token balance
   *   - 403: Access denied (not org admin)
   */
  async createGiftCard(
    orgId: string,
    request: CreateGiftCardRequest
  ): Promise<CreateGiftCardResponse> {
    assertOrgId(orgId);
    // Fetch raw response from backend (flat structure)
    const rawResponse = await this.post<RawCreateGiftCardResponse>(
      `/organizations/${orgId}/gift-cards`,
      request
    );

    // Transform to expected structure with wrapped giftCard
    // Construct a partial GiftCard with the fields returned by the API
    const giftCard: GiftCard = {
      giftCardId: rawResponse.giftCardId,
      tenantId: '', // Not returned by create endpoint
      sourceOrgId: orgId,
      sourceUserId: '', // Not returned by create endpoint
      amount: rawResponse.amount,
      status: rawResponse.status,
      expiresAt: rawResponse.expiresAt,
      createdAt: rawResponse.createdAt,
      updatedAt: rawResponse.createdAt,
      recipientEmail: request.recipientEmail,
      message: request.message,
    };

    return {
      giftCard,
      redemptionUrl: rawResponse.redemptionUrl,
    };
  }

  /**
   * Cancel a gift card
   *
   * HTTP Method: POST /organizations/:orgId/gift-cards/:giftCardId/cancel
   * Authorization: Required (JWT token)
   * RBAC: org_admin or global_admin only
   *
   * Cancels a pending gift card and returns the tokens to the organization's balance.
   * Only pending (unredeemed) gift cards can be cancelled.
   *
   * @param orgId - Organization ID
   * @param giftCardId - Gift card ID to cancel
   * @returns Cancelled gift card details
   * @throws {ApiError} When API request fails:
   *   - 400: Gift card already redeemed or cancelled
   *   - 403: Access denied (not org admin)
   *   - 404: Gift card not found
   */
  async cancelGiftCard(orgId: string, giftCardId: string): Promise<GiftCard> {
    assertOrgId(orgId);
    return this.post<GiftCard>(`/organizations/${orgId}/gift-cards/${giftCardId}/cancel`);
  }

  /**
   * Redeem a gift card using its ID
   *
   * HTTP Method: POST /gift-cards/redeem
   * Authorization: Required (JWT token)
   *
   * Redeems a gift card and credits the tokens to the current user's organization.
   * The gift card must be in 'pending' status and not expired.
   * Note: This endpoint does not require an orgId parameter.
   *
   * @param request - Object containing the gift card ID to redeem
   * @returns Redemption result with new balance
   * @throws {ApiError} When API request fails:
   *   - 400: Invalid giftCardId format
   *   - 404: Gift card not found
   *   - 410: Gift card already redeemed, cancelled, or expired
   */
  async redeemGiftCard(request: { giftCardId: string }): Promise<RedeemGiftCardResponse> {
    return this.post<RedeemGiftCardResponse>('/gift-cards/redeem', request);
  }
}

/**
 * Singleton instance export
 */
export const giftCardsApi = new GiftCardsApiClient();
